import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { createCode } from 'supertokens-auth-react/recipe/passwordless';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';

import { Logo } from '../assets/images';
import ErrorMessage from '../components/ErrorMessage';
import ModalFooter from '../components/ModalFooter';
import TopNav from '../components/TopNav';
import UserVerification from '../components/UserVerification';
import { getCookieDomain } from '../config';
import { captureLoginAttempt } from '../lib/utils/posthog';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionContext = useSessionContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>();

  if (sessionContext.loading === true) {
    return <TopNav />;
  }

  // Get our redirect URL from query params and validate it
  const getRedirectUrl = () => {
    const redirectUrl = searchParams.get('r');
    if (!redirectUrl) return null;

    const url = new URL(redirectUrl);
    const hostname = url.hostname;
    if (!hostname.endsWith(getCookieDomain().substring(1))) return null;

    return redirectUrl;
  };

  // Redirect away if already logged in and we have a redirect url
  if (sessionContext.doesSessionExist) {
    const redirectUrl = getRedirectUrl();
    if (redirectUrl) window.location.href = redirectUrl;
  }

  const handleSubmit = async (contact: string, type: string) => {
    setLoading(true);
    setError(null);

    try {
      let input: any = { email: contact };
      if (type === 'phone') {
        input = { phoneNumber: '+1 ' + contact }; // need to set number's country code
      }
      await createCode(input);
      captureLoginAttempt(contact, type, true);

      let redirectUrl = getRedirectUrl();
      if (!redirectUrl) redirectUrl = 'https://www.creditkey.com';
      navigate(['token', type, contact].join('/'), {
        state: { redirectUrl },
      });
    } catch (err: any) {
      setLoading(false);
      captureLoginAttempt(contact, type, false);

      // General errors are user facing input errors
      if (err.isSuperTokensGeneralError === true) {
        setError(err.message);
      } else {
        window.alert('Oops! Something went wrong.');
      }
    }
  };

  return (
    <>
      <TopNav />
      <div
        role="main"
        className="min-h-full flex flex-col justify-center xxs:py-0 lg:py-4 sm:px-6 lg:px-8"
      >
        <div className="mt-8 xxs:mx-auto xxs:w-full xxs:max-w-lg">
          <div className="bg-white my-4 xxs:py-10 sm:py-20 px-10 sm:shadow sm:rounded-lg sm:px-14 text-center">
            <div className="sm:flex justify-center mb-8 hidden">
              <img src={Logo} alt="Credit Key logo" />
            </div>
            <h2 className="my-2 text-3xl font-avenir-black text-navy-blue">
              Welcome to Credit Key
            </h2>
            <p className="my-2 text-base font-avenir-book text-opaque-navy">
              Please sign in. We'll send a 6-digit code to you.
            </p>
            {!loading && error && (
              <ErrorMessage className="mt-6" message={error} />
            )}
            <div className={classNames('mt-12', { 'mt-6': error })}>
              <UserVerification
                buttonText="Sign in"
                onSubmit={handleSubmit}
                loading={loading}
              />
            </div>
            <ModalFooter className="text-opaque-navy text-xs mt-8" />
          </div>
        </div>
      </div>
    </>
  );
}
